/* menu-weight-motion.js, 2026-09-04
   Letter cascade for the desktop header menu labels (option C of menu-anim/INTEGRATION.md).
   Scope: nav.header__inline-menu only. The drawer, the footer and View all are not touched.
   Each label at the three levels (top-level items incl. the simple links, tabs, tile captions) is rebuilt as
   i.wl > i.wl-w (word) > i.wl-c (letter, --i) > i.wl-r (the real letter, rest face) + i.wl-b (empty, data-text),
   with a plain-space i.wl-s between words so a wrapped caption still breaks per word. The label text therefore
   exists in the DOM exactly once (textContent and innerText read as on live); the bold face and the width-lock
   ghost are generated content (i.wl-b::before, i.wl-c::before). The 2026-09-04 block in george-hakim.css does
   the per-letter width lock and the opacity cascade; this file only builds the markup,
   sets --n (letter count) on the box and --nx (letters that had started going bold when the pointer left, so
   the return runs from the last bold letter), sets aria-label on the <a>, and re-measures the header Swipers
   after the split. Idempotent: spans already holding an i.wl are skipped; re-runs on shopify:section:load. */
(function () {
  'use strict';
  var NAV = 'nav.header__inline-menu';
  var LABELS = '.header__menu-item a > span, a.header__menu-item > span, li.ctm-tab-menu a > span, .ctm-tab-content li.swiper-slide a.mega-menu__link > span';
  var HOT_HOSTS = '.header__menu-item, .ctm_grandchild_menu li.swiper-slide a.mega-menu__link';
  var SWIPERS = '.ctm_childmenu.swiper, .swiper.ctm-tab-content';
  var STEP_MS = 28; /* the per-letter step in the CSS transition-delay */
  var seg = (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;

  function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Grapheme clusters when the browser can segment them, code points otherwise. */
  function graphemes(word) {
    if (seg) return Array.from(seg.segment(word), function (g) { return g.segment; });
    return Array.from(word);
  }

  /* One cell per letter; words are nowrap inline-blocks; a real space sits between words. */
  function cellsHTML(text) {
    var i = 0, html = '';
    text.split(' ').forEach(function (word, wi) {
      if (wi) html += '<i class="wl-s"> </i>';
      html += '<i class="wl-w">';
      graphemes(word).forEach(function (ch) {
        html += '<i class="wl-c" data-text="' + esc(ch) + '" style="--i:' + i + '">' +
          '<i class="wl-r">' + esc(ch) + '</i><i class="wl-b" data-text="' + esc(ch) + '"></i></i>';
        i++;
      });
      html += '</i>';
    });
    return { html: html, n: i };
  }

  function wrapLabels(nav) {
    nav.querySelectorAll(LABELS).forEach(function (span) {
      if (span.querySelector(':scope > i.wl')) return;
      var text = span.textContent.replace(/\s+/g, ' ').trim();
      if (text.normalize) text = text.normalize('NFC');
      if (!text) return;
      var cells = cellsHTML(text);
      var wl = document.createElement('i');
      wl.className = 'wl';
      wl.setAttribute('data-text', text);
      wl.setAttribute('aria-hidden', 'true');
      wl.style.setProperty('--n', String(cells.n));
      wl.innerHTML = cells.html;
      span.textContent = '';
      span.appendChild(wl);
      var a = span.closest('a');
      if (a && !a.hasAttribute('aria-label')) a.setAttribute('aria-label', text);
    });
  }

  /* Hot on: forget the previous return order and note when this cascade started. */
  function hotOn(wl) {
    if (!wl) return;
    wl.style.removeProperty('--nx');
    wl._since = performance.now();
  }
  /* Hot off: only the letters that had started going bold run back, last one first. */
  function hotOff(wl) {
    if (!wl) return;
    var n = parseInt(wl.style.getPropertyValue('--n'), 10) || 0;
    var went = Math.ceil((performance.now() - (wl._since || 0)) / STEP_MS); /* letters whose delay had elapsed */
    wl.style.setProperty('--nx', String(Math.max(1, Math.min(n, went))));
  }

  /* Top-level items and tile links are hot through :hover; mirror it for the --nx bookkeeping. */
  function hookHover(nav) {
    nav.querySelectorAll(HOT_HOSTS).forEach(function (el) {
      if (el._wlHooked) return;
      el._wlHooked = true;
      el.addEventListener('mouseenter', function () { hotOn(el.querySelector('i.wl')); });
      el.addEventListener('mouseleave', function () { hotOff(el.querySelector('i.wl')); });
    });
  }

  /* Tabs are hot through the is-active class toggled by the snippet's activateTab() (a closure), so watch
     the class attribute. The callback is a microtask, so it lands before the style recalc that starts the
     transition, and --nx is read with the right value. */
  function watchTabs(nav) {
    nav.querySelectorAll('li.ctm-tab-menu').forEach(function (li) {
      if (li._wlObserver) return;
      li._wlActive = li.classList.contains('is-active');
      var obs = new MutationObserver(function () {
        var on = li.classList.contains('is-active');
        if (on === li._wlActive) return;
        li._wlActive = on;
        var wl = li.querySelector('i.wl');
        if (on) hotOn(wl); else hotOff(wl);
      });
      obs.observe(li, { attributes: true, attributeFilter: ['class'] });
      li._wlObserver = obs;
    });
  }

  /* The split runs after the inline Swiper init in layout/theme.liquid, so re-measure the header strips. */
  function updateSwipers(nav) {
    nav.querySelectorAll(SWIPERS).forEach(function (el) {
      if (el.swiper && typeof el.swiper.update === 'function') el.swiper.update();
    });
  }

  function init(root) {
    var navs = (root || document).querySelectorAll(NAV);
    if (!navs.length) return;
    navs.forEach(function (nav) {
      wrapLabels(nav);
      hookHover(nav);
      watchTabs(nav);
      updateSwipers(nav);
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { navs.forEach(function (nav) { updateSwipers(nav); }); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
})();
